import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { setTokens } from '../../lib/tokens';

const YANDEX_CLIENT_ID = '9887e8773f1146498a8e44700daf09b5';
const SITE_ORIGIN = 'https://x-gis.xenus.dev';
const RELAY_URL = `${SITE_ORIGIN}/oauth/yandex-relay.html`;

declare global {
  interface Window {
    YaAuthSuggest?: {
      init: (
        oauthQueryParams: Record<string, string>,
        tokenPageOrigin: string,
        suggestParams: Record<string, string>,
      ) => Promise<{ handler: () => Promise<{ access_token: string }> }>;
    };
  }
}

/** Yandex ID's own widget — a real popup-based flow (not our server redirect):
 * the button opens oauth.yandex.ru in a popup, Yandex sends it back to
 * public/oauth/yandex-relay.html, which postMessages the token to this page.
 * We only see the finished access_token, which we hand to our backend to
 * verify and turn into a session. Code is the exact shape from Yandex's docs
 * (window.YaAuthSuggest.init(...).then(({handler}) => handler())...). */
export function YandexAuthButton() {
  const navigate = useNavigate();
  const containerId = `yandex-auth-button-${useId().replace(/[:]/g, '')}`;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!window.YaAuthSuggest) {
      setError('Виджет Яндекс ID не загрузился.');
      return;
    }

    let cancelled = false;

    window.YaAuthSuggest.init(
      {
        client_id: YANDEX_CLIENT_ID,
        response_type: 'token',
        redirect_uri: RELAY_URL,
      },
      SITE_ORIGIN,
      {
        view: 'button',
        parentId: containerId,
        buttonSize: 'm',
        buttonView: 'main',
        buttonTheme: 'light',
        buttonBorderRadius: '10',
        buttonIcon: 'ya',
      },
    )
      .then(({ handler }) => handler())
      .then(async (data) => {
        if (cancelled) return;
        const accessToken = data?.access_token;
        if (!accessToken) {
          setError('Яндекс не вернул токен доступа.');
          return;
        }
        const tokens = await api.yandexTokenLogin(accessToken);
        setTokens(tokens.access_token, tokens.refresh_token);
        navigate('/workspace', { replace: true });
      })
      .catch((err) => {
        if (cancelled) return;
        console.log('Ошибка входа через Яндекс', err);
        setError(err instanceof ApiError ? err.message : 'Не удалось войти через Яндекс.');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId]);

  return (
    <div>
      <div id={containerId} className="flex justify-center" />
      {error && <p className="mt-2 text-center text-xs font-medium text-status-warning">{error}</p>}
    </div>
  );
}
